import React, { useCallback, useEffect, useState } from 'react';
import { Modal, ModalBody, ModalHeader, ModalFooter } from '@this/shared/ui/feedbacks/modal';
import { Button } from '@this/shared/ui/inputs/button';
import { Textarea } from '@this/shared/ui/inputs/textarea';
import type { MessageTemplate } from '@this/components/arrangement/virtual_counter/types';
import { MessageBulkSendTemplateSelector } from './message_bulk_send_template_selector';

interface Props {
  isOpen: boolean;
  checkedCount: number;
  messageTemplates: MessageTemplate[];
  onClose: () => void;
  onSubmit: (body: string) => Promise<void>;
}

const MessageBulkSendModal: React.FC<Props> = ({ isOpen, checkedCount, messageTemplates, onClose, onSubmit }) => {
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setBody('');
      setSending(false);
    }
  }, [isOpen]);

  const handleSubmit = useCallback(async () => {
    if (sending) return;
    setSending(true);
    try {
      await onSubmit(body);
    } finally {
      setSending(false);
    }
  }, [body, sending, onSubmit]);

  const isDisabled = sending || checkedCount === 0 || body.trim().length === 0;

  return (
    <Modal open={isOpen} onClose={onClose}>
      <ModalHeader>メッセージ一括送信</ModalHeader>
      <ModalBody>
        <p style={{ marginBottom: '8px', fontSize: '12px', color: '#666' }}>
          ※ 宛名（〇〇様）は旅程ごとに自動で付与されます。本文のみを入力してください。
        </p>
        <Textarea
          data-testid="message-bulk-send-body"
          rowsMin={10}
          value={body}
          onChange={e => setBody(e.target.value)}
          placeholder="送信する本文を入力してください"
          fullWidth
        />
        <div style={{ marginTop: '8px' }}>
          <MessageBulkSendTemplateSelector
            messageTemplates={messageTemplates}
            onApply={content => setBody(content)}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button data-testid="message-bulk-send-cancel" onClick={onClose} disabled={sending}>
          キャンセル
        </Button>
        <Button
          data-testid="message-bulk-send-submit"
          onClick={handleSubmit}
          disabled={isDisabled}
          loading={sending}
        >
          {`${checkedCount}件に一括送付`}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default MessageBulkSendModal;
